import React, { useLayoutEffect } from "react";
import {
  Grid,
  Tabs,
  Tab,
  Drawer,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import {
  CreateButton,
  EditButton,
  FilterButton,
  List,
  RecordContextProvider,
  SelectInput,
  TopToolbar,
  useListContext,
  Loading,
  useGetList,
  required,
} from "react-admin";
import EditPage from "./EditPage";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";
import { useDispatch } from "react-redux";
import {
  setPopupPromotionsFilter,
  getPopupPromotionsFilter,
} from "src/slices/popupPromotionsPage/filterPopupPromotionsSlice";
import { usePersistFilter } from "src/hooks/usePersistFilter";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useSelector } from "react-redux";
import { LexoRank } from "lexorank";
import gql from "graphql-tag";
import Swal from "sweetalert2";
import Mbaas from "../../../provider/Mbaas";
import { useRetrievePermission } from "../../../hooks/useRetrievePermission";

const ListActions = ({ popupFilter }: any) => {
  usePersistFilter();
  const [isSuperAdmin, isAdmin] = useRetrievePermission();
  return (
    <TopToolbar>
      <FilterButton filters={popupFilter} />
      {(isSuperAdmin || isAdmin) && <CreateButton />}
    </TopToolbar>
  );
};

const ListPage = () => {
  const dispatch = useDispatch();

  const PopupFilter = [
    <SelectInput
      source="country"
      label="Country"
      choices={[
        { id: "HK", name: "Hong Kong" },
        { id: "TW", name: "Taiwan" },
        { id: "MY", name: "Malaysia" },
        { id: "ID", name: "Indonesia" },
      ]}
      validate={required()}
      alwaysOn
      data-testid="search-country"
      onChange={(e) =>
        //@ts-ignore
        dispatch(setPopupPromotionsFilter({ country: e.target.value }))
      }
    />,

    <SelectInput
      source="account_type"
      label="Platform"
      choices={[
        { id: "M21", name: "M21" },
        { id: "G2", name: "G2" },
        { id: "GHK_PREPAID", name: "GHK - Prepaid" },
        { id: "GHK_POSTPAID", name: "GHK - Postpaid" },
      ]}
      validate={required()}
      alwaysOn
      data-testid="search-platform"
      onChange={(e) =>
        //@ts-ignore
        dispatch(setPopupPromotionsFilter({ platform: e.target.value }))
      }
    />,
  ];

  return (
    <List
      perPage={999999}
      pagination={false}
      filterDefaultValues={{
        account_type: "M21",
      }}
      component="div"
      resource="promotionsPopup"
      title="Promotions &rsaquo; Popup"
      filters={PopupFilter}
      actions={<ListActions popupFilter={PopupFilter} />}
    >
      <LayoutDetail />
    </List>
  );
};

const LayoutDetail = () => {
  const dispatch = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();
  const { data, isLoading } = useListContext();
  const [items, setItems] = React.useState<Array<any>>([]);
  const [expand, setExpand] = React.useState<Array<string>>([]);
  const popupPromotionsFilter = useSelector(getPopupPromotionsFilter);
  const [value, setValue] = React.useState(0);
  const [isSuperAdmin, isAdmin] = useRetrievePermission();
  const canWrite = isSuperAdmin || isAdmin;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const { data: popupList, isLoading: popupLoading } = useGetList(
    "promotionsPopup",
    {
      filter: {
        type: value === 0 ? "ACTIVE" : value === 1 ? "INACTIVE" : "EXPIRED",
        country: popupPromotionsFilter.country,
        account_type: popupPromotionsFilter.platform,
      },
    }
  );

  useLayoutEffect(() => {
    setItems(popupList || []);
  }, [popupList]);

  const handleClose = React.useCallback(() => {
    navigate("/promotionsPopup");
  }, [navigate]);

  const matchEdit = matchPath("/promotionsPopup/:id", location.pathname);

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const StyledDialog = styled(Drawer)`
    .MuiBackdrop-root {
      background-color: rgba(0, 0, 0, 0.5);
    }
  `;

  const reorder = (list: Array<any>, startIndex: number, endIndex: number) => {
    let newKey;
    if (startIndex < endIndex) {
      const key = LexoRank.parse(list[endIndex].priority).genNext().toString();
      newKey = key;
    } else {
      const key = LexoRank.parse(list[endIndex].priority).genPrev().toString();
      newKey = key;
    }

    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    result[endIndex].priority = newKey;
    return { data: result[endIndex], newKey, result };
  };

  const updateRow = async (id: string, data: any) => {
    try {
      const req = await Mbaas.client.gql.mutation({
        mutation: gql`
          mutation updatePopupOrder(
            $ids: [UUID!]!
            $input: PromotionalBannersUpdateInput!
          ) {
            updatePromotionalBannersById(ids: $ids, input: $input) {
              id
              created_at
              updated_at
              priority
            }
          }
        `,
        variables: {
          ids: id,
          input: { priority: data.priority },
        },
      });

      console.log(req);
    } catch (e: any) {
      Swal.fire("Something wrong!", e.error.error, "error");
    }
  };

  const onDragEnd = async (result: any) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    const itemorder = reorder(items, sourceIndex, destIndex);

    setItems(itemorder.result);

    await updateRow(result.draggableId, itemorder.data);
  };

  return (
    <Grid container spacing={2} sx={{ marginTop: "1em", marginLeft: "0px" }}>
      <Grid container direction="column">
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          indicatorColor="secondary"
          textColor="inherit"
          variant="fullWidth"
        >
          <Tab
            sx={{
              bgcolor: value === 0 ? "#5e548e" : "#ffffff",
              color: value === 0 ? "#ffffff" : "#5e548e",
            }}
            label="Active"
            {...a11yProps(0)}
          />
          <Tab
            sx={{
              bgcolor: value === 1 ? "#5e548e" : "#ffffff",
              color: value === 1 ? "#ffffff" : "#5e548e",
            }}
            label="Inactive"
            {...a11yProps(1)}
          />
          <Tab
            sx={{
              bgcolor: value === 2 ? "#5e548e" : "#ffffff",
              color: value === 2 ? "#ffffff" : "#5e548e",
            }}
            label="Expired"
            {...a11yProps(2)}
          />
        </Tabs>
      </Grid>
      {popupLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            margin: "30px",
          }}
        >
          <CircularProgress style={{ width: "75px", height: "75px" }} />
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead
              sx={{ bgcolor: "#e7e7eb", color: "#5e548e", fontWeight: 700 }}
            >
              <TableRow>
                <TableCell
                  sx={{ color: "#5e548e", fontWeight: 700 }}
                  width={50}
                >
                  Order
                </TableCell>
                <TableCell
                  sx={{ color: "#5e548e", fontWeight: 700 }}
                  width={200}
                  align="center"
                >
                  Image
                </TableCell>
                <TableCell
                  sx={{ color: "#5e548e", fontWeight: 700 }}
                  width={300}
                  align="center"
                >
                  Title
                </TableCell>
                <TableCell
                  sx={{ color: "#5e548e", fontWeight: 700 }}
                  width={300}
                  align="center"
                >
                  Start Date
                </TableCell>
                <TableCell
                  sx={{ color: "#5e548e", fontWeight: 700 }}
                  width={300}
                  align="center"
                >
                  End Date
                </TableCell>
                {(isSuperAdmin || isAdmin) && (
                  <TableCell
                    sx={{ color: "#5e548e", fontWeight: 700 }}
                    width={300}
                    align="center"
                  >
                    Action
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
          </Table>
          <Droppable
            droppableId="dropppable"
            direction="vertical"
            type="parents"
          >
            {(provided, snapshot) => (
              <div {...provided.droppableProps}>
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {items?.map((data, index) => (
                    <RecordContextProvider key={data.id} value={data}>
                      <Draggable
                        key={data.id}
                        draggableId={data.id}
                        index={index}
                        isDragDisabled={!canWrite || expand.includes(data.id)}
                      >
                        {(provided, snapshot) => (
                          <TableRow
                            key={data.title}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                            {...provided.draggableProps}
                            ref={provided.innerRef}
                          >
                            <TableCell
                              width={50}
                              component="th"
                              scope="row"
                              {...provided.dragHandleProps}
                            >
                              {canWrite && <DragIndicatorIcon />}
                            </TableCell>
                            <TableCell width={200} align="center">
                              <img
                                src={data.image}
                                alt="icon"
                                style={{
                                  marginRight: 1,
                                  width: "200px",
                                  height: "100px",
                                }}
                              />
                            </TableCell>
                            <TableCell
                              width={300}
                              align="center"
                              style={{ fontWeight: 700 }}
                            >
                              {data.title}
                            </TableCell>
                            <TableCell width={300} align="center">
                              {format(new Date(data?.date_start), "dd-MM-yyyy")}
                            </TableCell>
                            <TableCell width={300} align="center">
                              {format(new Date(data?.date_end), "dd-MM-yyyy")}
                            </TableCell>
                            {(isSuperAdmin || isAdmin) && (
                              <TableCell width={300} align="center">
                                <EditButton
                                  label="Edit"
                                  size="medium"
                                  data-testid={`edit-btn-${data.title}`}
                                  color="primary"
                                  variant="outlined"
                                  style={{
                                    textTransform: "none",
                                  }}
                                />
                              </TableCell>
                            )}
                          </TableRow>
                        )}
                      </Draggable>
                    </RecordContextProvider>
                  ))}
                </TableBody>

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
      <StyledDialog
        open={!!matchEdit}
        anchor="right"
        onClose={handleClose}
        sx={{ zIndex: 100 }}
      >
        {!!matchEdit && <EditPage id={(matchEdit as any).params?.id} />}
      </StyledDialog>
    </Grid>
  );
};

export default ListPage;
